/* eslint-disable prefer-spread */
import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import {
  DownloadJobDescriptionForViewer,
  DownloadJobDescriptionOriginal,
  UploadJobDescription,
} from 'services/JobsService';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import { GetSettingBySettingName, UpdateSettingBySettingName } from 'services/UsersService';
import { RegexIsPositiveNumberWith2Decimals } from 'util/RegExUtils';
import UploadDescriptionDialog from 'components/Dialogs/Jobs/UploadDescriptionDialog';
import FilePicker from 'components/Pickers/FilePicker';
import Button from '@mui/material/Button';

interface Props {
  jobId: number;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

const settingName = 'DocumentScaleRatio';

export default function JobDescriptionRenderer({
  jobId,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [fileData, setFileData] = useState('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>();
  const [scale, setScale] = useState(2);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [noDescriptionFound, setNoDescriptionFound] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    const getDocScaleSetting = async () => {
      const res = await GetSettingBySettingName(settingName);
      if (res && RegexIsPositiveNumberWith2Decimals(res)) setScale(+res);
    };
    getDocScaleSetting();
  }, []);

  const onScaleChangeHandler = useCallback((value: number) => {
    setScale((prev) => {
      const tmp = prev + value;
      if (tmp <= 0) return prev;
      UpdateSettingBySettingName(settingName, tmp.toFixed(2).toString());
      return tmp;
    });
  }, []);

  const onZoomOut = useCallback(() => {
    onScaleChangeHandler(-0.2);
  }, [onScaleChangeHandler]);

  const onZoomIn = useCallback(() => {
    onScaleChangeHandler(0.2);
  }, [onScaleChangeHandler]);

  const downloadOriginalJobDescription = useCallback(async () => {
    loadingHandler && loadingHandler(true);
    await DownloadJobDescriptionOriginal(jobId, undefined, errorHandler);
    loadingHandler && loadingHandler(false);
  }, [jobId, loadingHandler, errorHandler]);

  const getFile = useCallback(async () => {
    if (jobId) {
      loadingHandler?.(true);
      setLoadingMessage(undefined);
      setFileData('');
      const res = await DownloadJobDescriptionForViewer(jobId);
      if (res) {
        const reader = new FileReader();
        reader.readAsDataURL(res.data);
        reader.onloadend = () => {
          const r = reader.result as string;
          setFileData(r.substring(r.indexOf(',') + 1));
        };
      } else setNoDescriptionFound(true);
      loadingHandler?.(false);
    }
  }, [jobId, loadingHandler]);

  useEffect(() => {
    getFile();
  }, [getFile]);

  const uploadSuccessHandler = useCallback(
    (message: string) => {
      successHandler?.(message);
      getFile();
    },
    [getFile, successHandler]
  );

  const removeFileCallback = useCallback(() => {
    setFiles([]);
  }, []);

  const uploadDescriptionHandler = useCallback(async () => {
    loadingHandler?.(true);
    const res = await UploadJobDescription(jobId, files, errorHandler);
    if (res) {
      successHandler?.('Description File Uploaded');
      setNoDescriptionFound(false);
      await getFile();
    }
    loadingHandler?.(false);
  }, [jobId, files, errorHandler, loadingHandler, successHandler, getFile]);

  return (
    <>
      <UploadDescriptionDialog
        jobId={jobId}
        open={showUploadDialog}
        closeHandler={() => setShowUploadDialog(false)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={uploadSuccessHandler}
      />
      <Box
        display={noDescriptionFound ? 'none' : 'flex'}
        flex="1"
        flexDirection="column"
        position="relative"
      >
        <Box ml="auto">
          <Box component="span" display={loadingMessage ? 'none' : 'flex'} gap="5px" mr={2}>
            <Avatar
              onClick={onZoomOut}
              sx={{ cursor: 'pointer', bgcolor: (t) => t.palette.secondary.main }}
              title="Zoom Out"
            >
              <RemoveIcon />
            </Avatar>
            <Avatar
              onClick={onZoomIn}
              sx={{ cursor: 'pointer', bgcolor: (t) => t.palette.secondary.main }}
              title="Zoom In"
            >
              <AddIcon />
            </Avatar>
            <Avatar
              onClick={downloadOriginalJobDescription}
              sx={{ cursor: 'pointer', bgcolor: (t) => t.palette.primary.main }}
              title="Download"
            >
              <DownloadIcon />
            </Avatar>
            <Button variant="contained" onClick={() => setShowUploadDialog(true)}>
              Replace Description
            </Button>
          </Box>
        </Box>

        <Box display="flex" flex="1" flexDirection="column" position="relative" overflow="hidden">
          <Box position="absolute" top="0" left="0" height="100%" width="100%" overflow="auto">
            <Box display="flex" justifyContent="center">
              <Document
                file={`data:application/pdf;base64,${fileData}`}
                onLoadSuccess={(p) => setNumPages(p.numPages)}
                loading={loadingMessage}
              >
                <Stack spacing={2}>
                  {Array.apply(null, Array(numPages))
                    .map((x, i) => i + 1)
                    .map((page) => (
                      <Page key={page} pageNumber={page} scale={scale} />
                    ))}
                </Stack>
              </Document>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box display={noDescriptionFound ? 'block' : 'none'} textAlign="center">
        <FilePicker
          singleFile
          files={files}
          zoneText="Drop file to upload or click"
          addFilesHandler={setFiles}
          removeFileHandler={removeFileCallback}
        />
        {files.length === 1 && (
          <Button variant="contained" onClick={uploadDescriptionHandler}>
            Upload file
          </Button>
        )}
      </Box>
    </>
  );
}
